import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { getHttpClient, useHttpClient } from '@bonusly/http-client';
import { useIdentity } from '@bonusly/who-am-i';

import { getQueryClient } from '../lib/query-client';
import { DistributionMethod, ItemType, Provider, Reward, RewardDetail } from '../schemas/rewards';
import { listOf } from '../schemas/utils';
import { toSnakeCaseKeys } from '../utils/object';
import { headers, Metadata, MutationOptions, PaginatedData, PaginationParams } from './meta';

export const QueryKey = {
	AdminRewards: 'rewards:admin-rewards',
	AxomoToken: 'rewards:axomo-token',
	CatalogSettings: 'rewards:catalog-settings',
	CollectionItems: 'rewards:collection-items',
	Collections: 'rewards:collections',
	ExclusiveReward: 'rewards:exclusive-reward',
	Filters: 'rewards:filters',
	Highlights: 'rewards:highlights',
	ItemCategories: 'rewards:item-categories',
	ItemSubcategories: 'rewards:item-subcategories',
	PaymentStatus: 'rewards:payment-status',
	PostWrapReward: 'rewards:post-wrap-reward',
	RewardRedemptions: 'rewards:reward-redemptions',
	Rewards: 'rewards:rewards',
	RewardsMetadata: 'rewards:metadata',
} as const;

export const rewardRedemptionsQueryKey = (params?: RewardRedemptionAPIParams) => [QueryKey.RewardRedemptions, params].filter(Boolean);
export const rewardRedemptionQueryKey = (id: string) => [QueryKey.RewardRedemptions, id];
export const itemSubcategoriesQueryKey = () => [QueryKey.ItemSubcategories];
export const filtersQueryKey = () => [QueryKey.Filters];
export const highlightsQueryKey = () => [QueryKey.Highlights];
export const rewardsQueryKey = (params?: RewardsAPIParams) => [QueryKey.Rewards, params].filter(Boolean);
export const rewardQueryKey = (id: string) => [QueryKey.Rewards, id];
export const settingsQueryKey = () => [QueryKey.CatalogSettings];
export const collectionsQueryKey = () => [QueryKey.Collections];
export const collectionQueryKey = (id: string) => [QueryKey.Collections, id];
export const collectionItemsQueryKey = (id: string, params?: Record<string, any>) => [QueryKey.CollectionItems, id, params].filter(Boolean);
export const axomoTokenQueryKey = () => [QueryKey.AxomoToken];
export const adminRewardsQueryKey = (params?: AdminRewardsAPIParams) => [QueryKey.AdminRewards, params].filter(Boolean);
export const metadataQueryKey = () => [QueryKey.RewardsMetadata];
export const swagIntegrationQueryKey = () => [QueryKey.RewardsMetadata, 'swag_integration'];
export const exclusiveRewardQueryKey = (id: string) => [QueryKey.ExclusiveReward, id];
export const exclusiveRewardsQueryKey = () => [QueryKey.ExclusiveReward];
export const postWrapRewardQueryKey = () => [QueryKey.PostWrapReward];
export const paymentStatusQueryKey = (paymentIntentId: string | null) => [QueryKey.PaymentStatus, paymentIntentId].filter(Boolean);

// This one is for creating a redemption in the top level controller
export const redemptionsPath = (prefix = '') => `${prefix}/redemptions`;
// This one follows newer patterns and is part of the rewards namespace
export const rewardRedemptionsPath = (prefix = '') => `${prefix}/rewards/redemptions`;
export const rewardRedemptionPath = (id: string, prefix = '') => `${prefix}/rewards/redemptions/${id}`;
export const resendGiftEmailPath = (id: string, prefix = '') => `${prefix}/rewards/redemptions/${id}/resend`;
export const itemSubcategoriesPath = (prefix = '') => `${prefix}/catalogs/swag_sub_categories`;
export const filtersPath = (prefix = '') => `${prefix}/rewards/filters`;
export const highlightsPath = (prefix = '') => `${prefix}/rewards/highlights`;
export const rewardsPath = (prefix = '') => `${prefix}/rewards`;
export const rewardPath = (id: string, prefix = '') => `${prefix}/rewards/${id}`;
export const settingsPath = (prefix = '') => `${prefix}/admin/settings`;
export const collectionsPath = (prefix = '') => `${prefix}/admin/collections`;
export const collectionPath = (id: string, prefix = '') => `${prefix}/admin/collections/${id}`;
export const collectionItemsPath = (id: string, prefix = '') => `${prefix}/admin/collections/${id}/rewards`;
export const axomoTokenPath = (prefix = '') => `${prefix}/axomo_token_generator`;
export const adminRewardsPath = (prefix = '') => `${prefix}/admin/rewards`;
export const adminRewardPath = (id: string, prefix = '') => `${prefix}/admin/rewards/${id}`;
export const metadataPath = (prefix = '') => `${prefix}/admin/metadata/catalog_totals`;
export const swagIntegrationPath = (prefix = '') => `${prefix}/admin/metadata/swag_integration`;
export const exclusiveRewardsPath = (prefix = '') => `${prefix}/admin/exclusive_rewards`;
export const exclusiveRewardPath = (id: string, prefix = '') => `${prefix}/admin/exclusive_rewards/${id}`;
export const createPaymentIntentPath = (prefix = '') => `${prefix}/user/billing/payment_intents`;
export const paymentStatusPath = (prefix = '') => `${prefix}/user/billing/payment_status`;

export const loadAxomoToken = async () => {
	const queryClient = await getQueryClient();
	const http = await getHttpClient();

	return queryClient.fetchQuery({
		queryKey: axomoTokenQueryKey(),
		queryFn: async () => (await http.get<{ token: string }>(axomoTokenPath())).data.token,
	});
};

export type RedemptionAPIData = {
	redemption: {
		id: string;
	};
};

export type CreateRedemptionAPIData = {
	color?: string;
	denominationId?: string;
	fulfillmentPartner?: string;
	giftEmail?: string;
	giftFirstName?: string;
	giftMessage?: string;
	isGift: boolean;
	shippingAddressAttributes?: {
		address1?: string;
		address2?: string;
		city?: string;
		state?: string;
		country?: string;
		postalCode?: string;
		phoneNumber?: string;
	};
	size?: string;
	variantData?: {
		type: string;
		pattern?: string;
		color?: string;
		post_wrap_image_id?: string;
	};
};

type MutateRedemptionsOptions = {
	onError?: (error: unknown) => void;
	onMutate?: (data: CreateRedemptionAPIData) => void;
	onSuccess?: (celebration: RedemptionAPIData) => void;
};

export type SyncClientStateAfterRedemptionArgs = {
	queryClient: QueryClient;
	refetchProfile: () => unknown;
	invalidateRedemptions: () => void;
	invalidateRewards: () => void;
};

/**
 * Refreshes identity (`earning_balance`) and rewards-related caches after a redemption completes.
 * Shared by points-only `useClaimReward` and credit-card top-off (payment polling) flows.
 */
export function syncClientStateAfterRedemption({
	queryClient,
	refetchProfile,
	invalidateRedemptions,
	invalidateRewards,
}: SyncClientStateAfterRedemptionArgs): void {
	// Note: legacy query keys — kept aligned with historical invalidation behavior.
	queryClient.invalidateQueries(['recognition-user']);
	queryClient.invalidateQueries(['rewards-user']);
	queryClient.invalidateQueries(['navbar-profile']);

	void refetchProfile();
	invalidateRedemptions();
	invalidateRewards();
}

export const useClaimReward = (options: MutateRedemptionsOptions = {}) => {
	const http = useHttpClient();
	const queryClient = useQueryClient();
	const invalidateRedemptions = useInvalidateRedemptions();
	const invalidateRewards = useInvalidateRewards();
	const { refetchProfile } = useIdentity();

	return useMutation({
		mutationFn: async (data: CreateRedemptionAPIData) => {
			try {
				options.onMutate?.(data);
				const res = await http.post<{ result: RedemptionAPIData }>(redemptionsPath(), data, { headers });
				options.onSuccess?.(res.data.result);

				syncClientStateAfterRedemption({
					queryClient,
					refetchProfile,
					invalidateRedemptions,
					invalidateRewards,
				});

				return res.data.result;
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

export type PostWrapImageUploadResult = {
	id: string;
	url: string;
};

export const useUploadPostWrapImage = () => {
	const http = useHttpClient();

	return useMutation({
		mutationFn: async (blob: Blob): Promise<PostWrapImageUploadResult> => {
			const formData = new FormData();
			formData.append('file', blob, 'post-wrap.jpg');
			const res = await http.post<PostWrapImageUploadResult>('/users/post_wrap_images', formData);
			return res.data;
		},
	});
};

// Catalog Metadata
export type CatalogMetadataAPIData = {
	totalAvailableItems: number;
	totalHiddenItems: number;
	exclusiveItemsCount: number;
	swagItemsCount: number;
	activeCountries: string[]; // ISO country codes
};

const createMetadataFetcher = () => async () => {
	const http = await getHttpClient('rcs');
	return (await http.get<CatalogMetadataAPIData>(metadataPath(), { headers })).data;
};

export const loadCatalogMetadata = async () => {
	const queryClient = await getQueryClient();

	return queryClient.fetchQuery({
		queryKey: metadataQueryKey(),
		queryFn: createMetadataFetcher(),
	});
};

export const useCatalogMetadata = () => {
	return useQuery({
		queryKey: metadataQueryKey(),
		queryFn: createMetadataFetcher(),
	});
};

// Swag Integration Metadata
export type SwagIntegrationAPIData = {
	integration: {
		provider: string;
		storeUrl: string;
	} | null;
};

const createSwagIntegrationFetcher = () => async () => {
	const http = await getHttpClient('rcs');
	return (await http.get<SwagIntegrationAPIData>(swagIntegrationPath(), { headers })).data;
};

export const loadSwagIntegration = async () => {
	const queryClient = await getQueryClient();

	return queryClient.fetchQuery({
		queryKey: swagIntegrationQueryKey(),
		queryFn: createSwagIntegrationFetcher(),
	});
};

export const useSwagIntegration = () => {
	return useQuery({
		queryKey: swagIntegrationQueryKey(),
		queryFn: createSwagIntegrationFetcher(),
	});
};

export type RewardRedemptionAPIData = {
	id: string;
	aasmState: 'new' | 'approved' | 'fulfilled' | 'declined' | 'refunded';
	brandName: string;
	claimUrl: string | null;
	color: string | null;
	createdAt: string;
	donation: boolean;
	gift: boolean;
	giftCard: boolean;
	gifteeEmail: string | null;
	name: string;
	priceInCurrency: number;
	priceInPoints: number;
	redemptionInstructions: string | null;
	size: string | null;
	catalogItem: {
		id: string;
		denomination: {
			id: string;
			optionName: string;
			optionType: string;
			quantity: number | null;
			distributionMethod: DistributionMethod;
			provider: Provider;
		} | null;
		imageUrl: string;
		type: 'axomo' | 'built_in' | 'custom' | 'other';
	};
	redemptionRecords: {
		id: string;
		amount: number;
		createdAt: string;
		state: 'new' | 'manual_pending' | 'manual_fulfilled' | 'succeeded' | 'failed' | 'canceled' | 'retry' | 'out_of_stock' | 'pending_voucher' | 'expired';
		transactionFee: number | null;
		errorMessage: string | null;
		pin: string | null;
		code: string | null;
		number: string | null;
		orderId: string | null;
		redemptionUrl: string | null;
		barcodeImageUrl: string | null;
		certificateUrl: string | null;
		expirationDate: string | null;
	}[];
};

export type RewardRedemptionAPIParams = PaginationParams;

const createRewardRedemptionsFetcher = (params?: RewardRedemptionAPIParams) => async () => {
	const http = await getHttpClient();
	const res = await http.get<PaginatedData<RewardRedemptionAPIData>>(rewardRedemptionsPath(), { headers, params });

	return [res.data.records, new Metadata(res.data.metadata)] as const;
};

export const loadRewardRedemptions = async (params?: RewardRedemptionAPIParams) => {
	const queryClient = await getQueryClient();
	return queryClient.fetchQuery({
		queryKey: rewardRedemptionsQueryKey(params),
		queryFn: createRewardRedemptionsFetcher(params),
	});
};

export const useRewardRedemptions = (params?: RewardRedemptionAPIParams) => {
	return useQuery({
		queryKey: rewardRedemptionsQueryKey(params),
		queryFn: createRewardRedemptionsFetcher(params),
	});
};

export type RewardRedemptionDetailAPIData = RewardRedemptionAPIData & {
	shippingAddress: {
		address1: string;
		address2: string;
		city: string;
		state: string;
		postalCode: string;
		country: string;
	} | null;
};

const createRewardRedemptionFetcher = (id: string) => async () => {
	const http = await getHttpClient();
	return (await http.get<RewardRedemptionDetailAPIData>(rewardRedemptionPath(id), { headers })).data;
};

export const loadRewardRedemption = async (id: string) => {
	const queryClient = await getQueryClient();
	return queryClient.fetchQuery({
		queryKey: rewardRedemptionQueryKey(id),
		queryFn: createRewardRedemptionFetcher(id),
	});
};

export const useRewardRedemption = (id?: string) => {
	return useQuery({
		queryKey: rewardRedemptionQueryKey(id as string),
		queryFn: createRewardRedemptionFetcher(id as string),
		enabled: !!id,
		keepPreviousData: false,
	});
};

export const useInvalidateRedemptions = () => {
	const queryClient = useQueryClient();
	return () => {
		queryClient.invalidateQueries(rewardRedemptionsQueryKey());
	};
};

export const useInvalidateRedemption = () => {
	const queryClient = useQueryClient();
	return (id: string) => {
		queryClient.invalidateQueries(rewardRedemptionQueryKey(id));
	};
};

type MutateGiftReceipintOptions = {
	onError?: (error: unknown) => void;
	onSuccess?: (celebration: RewardRedemptionAPIData) => void;
};

export const useUpdateGiftRecipient = (options: MutateGiftReceipintOptions = {}) => {
	const http = useHttpClient();
	const invalidate = useInvalidateRedemption();

	return useMutation({
		mutationFn: async ({ id, email }: { id: string; email: string }) => {
			try {
				const res = await http.post<RewardRedemptionAPIData>(resendGiftEmailPath(id), { email }, { headers });
				options.onSuccess?.(res.data);
				invalidate(id);
				return res.data;
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

type ItemSubcategoriesAPIData = {
	data: string[];
};

const createItemSubcategoriesFetcher = () => async () => {
	const http = await getHttpClient('rcs');
	const res = await http.get<ItemSubcategoriesAPIData>(itemSubcategoriesPath(), { headers });

	return res.data.data;
};

export const loadItemSubcategories = async () => {
	const queryClient = await getQueryClient();
	return queryClient.fetchQuery({
		queryKey: itemSubcategoriesQueryKey(),
		queryFn: createItemSubcategoriesFetcher(),
	});
};

export const useItemSubcategories = () => {
	return useQuery({
		queryKey: itemSubcategoriesQueryKey(),
		queryFn: createItemSubcategoriesFetcher(),
	});
};

// Updated rewards index handlers

export type FitlersAPIData = {
	collections: {
		key: 'featured' | 'exclusive' | 'swag' | 'all' | 'no_cost';
		id: string | null;
		name: string | null;
	}[];
	filters: {
		key: 'category' | 'price' | 'type';
		options: { key: string }[];
	}[];
};

const createFiltersFetcher = () => async () => {
	const http = await getHttpClient('rcs');
	return (await http.get<FitlersAPIData>(filtersPath(), { headers })).data;
};

export const loadFilters = async () => {
	const queryClient = await getQueryClient();
	return queryClient.fetchQuery({
		queryKey: filtersQueryKey(),
		queryFn: createFiltersFetcher(),
	});
};

export const useFilters = () => {
	return useQuery({
		queryKey: filtersQueryKey(),
		queryFn: createFiltersFetcher(),
	});
};

export const useInvalidateFilters = () => {
	const queryClient = useQueryClient();

	return useCallback(() => {
		queryClient.invalidateQueries(filtersQueryKey());
	}, [queryClient]);
};

type RewardsAPIParams = {
	q?: string | null;
	filters?: {
		collection?: string;
		category?: string[];
		price?: string[];
		type?: string[];
		subCategory?: string;
	};
	pageNumber?: number;
};

export type RewardsAPIData = PaginatedData<{
	id: string;
	name: string;
	minAmountInPoints: number;
	maxAmountInPoints: number;
	labels: string[];
}>;

export const useInvalidateRewards = () => {
	const queryClient = useQueryClient();
	return useCallback(() => {
		queryClient.invalidateQueries(rewardsQueryKey());
	}, [queryClient]);
};

const createRewardsFetcher =
	(params: RewardsAPIParams = {}) =>
	async () => {
		const http = await getHttpClient('rcs');

		// Add pageSize and transform keys to snake_case
		const apiParams = toSnakeCaseKeys({
			...params,
			pageSize: 24,
		});

		const res = (await http.get<RewardsAPIData>(rewardsPath(), { headers, params: apiParams })).data;

		return [listOf(Reward).parse(res.records), new Metadata(res.metadata)] as const;
	};

export const loadRewards = async (params: RewardsAPIParams = {}) => {
	const queryClient = await getQueryClient();
	return queryClient.fetchQuery({
		queryKey: rewardsQueryKey(params),
		queryFn: createRewardsFetcher(params),
	});
};

export const useRewards = (params: RewardsAPIParams = {}) => {
	return useQuery({
		queryKey: rewardsQueryKey(params),
		queryFn: createRewardsFetcher(params),
	});
};

export const useReward = (id: string = '') => {
	const http = useHttpClient('rcs');

	return useQuery({
		queryKey: rewardQueryKey(id),
		queryFn: async () => {
			const res = (await http.get(rewardPath(id), { headers })).data;
			return RewardDetail.parse(res);
		},
		enabled: !!id,
		keepPreviousData: false,
	});
};

// Highlights

export type HighlightsAPIData = {
	promoted: Array<{
		id: string;
		name: string;
		minAmountInPoints: number;
		maxAmountInPoints: number;
		labels: string[];
	}>;
	recent: Array<{
		id: string;
		name: string;
		minAmountInPoints: number;
		maxAmountInPoints: number;
		labels: string[];
	}>;
};

const createHighlightsFetcher = () => async () => {
	const http = await getHttpClient('rcs');
	const res = await http.get<HighlightsAPIData>(highlightsPath(), { headers, params: { limit: 5 } });

	return {
		promoted: listOf(Reward).parse(res.data.promoted),
		recent: listOf(Reward).parse(res.data.recent),
	};
};

export const loadHighlights = async () => {
	const queryClient = await getQueryClient();
	return queryClient.fetchQuery({
		queryKey: highlightsQueryKey(),
		queryFn: createHighlightsFetcher(),
	});
};

export const useHighlights = () => {
	return useQuery({
		queryKey: highlightsQueryKey(),
		queryFn: createHighlightsFetcher(),
	});
};

// Post Wrap Reward

const createPostWrapRewardFetcher = () => async () => {
	const http = await getHttpClient('rcs');

	const apiParams = toSnakeCaseKeys({
		filters: { type: ['collectible'] },
		pageSize: 24,
	});

	const res = (await http.get<RewardsAPIData>(rewardsPath(), { headers, params: apiParams })).data;
	const rewards = listOf(Reward).parse(res.records);

	return rewards.find((r) => r.distributionMethod === DistributionMethod.Collectible) ?? null;
};

export const loadPostWrapReward = async () => {
	const queryClient = await getQueryClient();
	return queryClient.fetchQuery({
		queryKey: postWrapRewardQueryKey(),
		queryFn: createPostWrapRewardFetcher(),
	});
};

export const usePostWrapReward = () => {
	return useQuery({
		queryKey: postWrapRewardQueryKey(),
		queryFn: createPostWrapRewardFetcher(),
	});
};

// Admin queries

type CatalogSettingsAPIData = {
	giftCardsEnabled: boolean;
	donationsEnabled: boolean;
	curatedGiftsEnabled: boolean;
	collectiblesEnabled: boolean;
	autoAddNewRewards: boolean;
	maxPriceInUsdCents: number | null;
	autoApproveMax: number;
	userPaymentTopoffEnabled: boolean;
};

const createSettingsFetcher = () => async () => {
	const http = await getHttpClient('rcs');
	return (await http.get<CatalogSettingsAPIData>(settingsPath(), { headers })).data;
};

export const loadCatalogSettings = async () => {
	const queryClient = await getQueryClient();
	return queryClient.fetchQuery({
		queryKey: settingsQueryKey(),
		queryFn: createSettingsFetcher(),
	});
};

export const useCatalogSettings = () => {
	return useQuery({
		queryKey: settingsQueryKey(),
		queryFn: createSettingsFetcher(),
	});
};

export const useStoreCatalogSettings = () => {
	const queryClient = useQueryClient();

	return useCallback(
		(record: CatalogSettingsAPIData) => {
			queryClient.setQueryData<CatalogSettingsAPIData>(settingsQueryKey(), record);
		},
		[queryClient]
	);
};

export type MutateSettingsOptions = {
	onError?: (error: unknown) => void;
	onSuccess?: (record: CatalogSettingsAPIData) => void;
};

export const useUpdateCatalogSettings = (options: MutateSettingsOptions = {}) => {
	const http = useHttpClient('rcs');
	const store = useStoreCatalogSettings();

	return useMutation({
		mutationFn: async (params: CatalogSettingsAPIData) => {
			try {
				const res = (await http.patch<CatalogSettingsAPIData>(settingsPath(), params, { headers })).data;
				store(res);
				options.onSuccess?.(res);
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

// Collection management

export type CollectionAPIData = {
	id: string;
	name: string;
	state: 'active' | 'archived';
};

export type CollectionDetailAPIData = CollectionAPIData & {
	itemCount: number;
};

const createCollectionFetcher = (id: string) => async () => {
	const http = await getHttpClient('rcs');
	return (await http.get<CollectionAPIData>(collectionPath(id), { headers })).data;
};

export const loadCollection = async (id: string) => {
	const queryClient = await getQueryClient();
	return queryClient.fetchQuery({
		queryKey: collectionQueryKey(id),
		queryFn: createCollectionFetcher(id),
	});
};

export const useCollection = (id: string) => {
	return useQuery({
		queryKey: collectionQueryKey(id),
		queryFn: createCollectionFetcher(id),
	});
};

const createCollectionsFetcher = () => async () => {
	const http = await getHttpClient('rcs');
	return (await http.get<CollectionDetailAPIData[]>(collectionsPath(), { headers })).data;
};

export const loadCollections = async () => {
	const queryClient = await getQueryClient();
	return queryClient.fetchQuery({
		queryKey: collectionsQueryKey(),
		queryFn: createCollectionsFetcher(),
	});
};

export const useCollections = () => {
	return useQuery({
		queryKey: collectionsQueryKey(),
		queryFn: createCollectionsFetcher(),
	});
};

export const useInvalidateCollections = () => {
	const queryClient = useQueryClient();

	return useCallback(() => {
		queryClient.invalidateQueries(collectionsQueryKey());
	}, [queryClient]);
};

type MutateCollectionOptions = {
	onError?: (error: unknown) => void;
	onSuccess?: (record: CollectionAPIData) => void;
};

export const useArchiveCollection = (options: MutateCollectionOptions = {}) => {
	const http = useHttpClient('rcs');
	const invalidateCollections = useInvalidateCollections();
	const invalidateFilters = useInvalidateFilters();

	return useMutation({
		mutationFn: async (id: string) => {
			try {
				const res = (await http.patch<CollectionAPIData>(collectionPath(id), { state: 'archived' }, { headers })).data;
				options.onSuccess?.(res);
				invalidateCollections();
				invalidateFilters();

				return res;
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

export const useActivateCollection = (options: MutateCollectionOptions = {}) => {
	const http = useHttpClient('rcs');
	const invalidateCollections = useInvalidateCollections();
	const invalidateFilters = useInvalidateFilters();

	return useMutation({
		mutationFn: async (id: string) => {
			try {
				const res = (await http.patch<CollectionAPIData>(collectionPath(id), { state: 'active' }, { headers })).data;
				options.onSuccess?.(res);
				invalidateCollections();
				invalidateFilters();

				return res;
			} catch (e) {
				options.onError?.(e);
				throw e;
			}
		},
	});
};

// Collection details

type CollectionItemAPIParams = {};

type CollectionItemAPIData = {
	id: string;
	name: string;
	categories: string[];
	minPriceUsd: number;
	maxPriceUsd: number;
	imageUrl: string;
	state: 'active' | 'inactive' | 'archived';
	status: 'active' | 'archived' | 'inactive' | 'admin_disabled' | 'discontinued' | 'needs_attention' | 'out_of_stock' | 'system_disabled';
};

const createCollectionItemsFetcher =
	(id: string, params: CollectionItemAPIParams = {}) =>
	async () => {
		const http = await getHttpClient('rcs');
		const apiParams = toSnakeCaseKeys(params);
		const res = (await http.get<PaginatedData<CollectionItemAPIData>>(collectionItemsPath(id), { headers, params: apiParams })).data;

		return [res.records, new Metadata(res.metadata)] as const;
	};

export const loadCollectionItems = async (id: string, params: CollectionItemAPIParams = {}) => {
	const queryClient = await getQueryClient();
	return queryClient.fetchQuery({
		queryKey: collectionItemsQueryKey(id, params),
		queryFn: createCollectionItemsFetcher(id, params),
	});
};

export const useCollectionItems = (id: string, params: CollectionItemAPIParams = {}) => {
	return useQuery({
		queryKey: collectionItemsQueryKey(id, params),
		queryFn: createCollectionItemsFetcher(id, params),
	});
};

// Admin

type AdminRewardsAPIParams = {
	pageNumber: number;
	q?: string | null;
	type?: string[];
	category?: string[];
	state?: string[];
	countryCode?: string | null;
	collection?: string | null;
	sortBy?: string | null;
	sortDirection?: 'ASC' | 'DESC' | null;
};

export type AdminRewardAPIData = {
	id: string;
	bonuslyPromoted: boolean;
	brandName: string;
	categories: Array<{ id: string; key: string; name: string }>;
	companyPromoted: boolean;
	countryCodes: string[];
	distributionMethod: DistributionMethod;
	imageAltText: string;
	imageUrl: string;
	itemId: string;
	maxPriceInPoints: number | null;
	maxPriceInUsd: number | null;
	minPriceInPoints: number | null;
	minPriceInUsd: number | null;
	name: string;
	outOfStockVariants: Array<{ id: string; priceInUsd: number }>;
	provider: Provider | null;
	state: 'active' | 'inactive';
	status: 'normal' | 'out_of_stock' | 'partial_stock';
	type: ItemType;
};

const createAdminRewardFetcher =
	(params: AdminRewardsAPIParams = { pageNumber: 1 }) =>
	async () => {
		const http = await getHttpClient('rcs');

		// Transform params: q, type, category, state, countryCode, and collection become filters for the API
		// sortBy and sortDirection are passed as top-level params
		const { q, type, category, state, countryCode, collection, sortBy, sortDirection, ...restParams } = params;

		// Build filters object if any filter params exist
		const filters: Record<string, any> = {};
		if (q) filters.search = q;
		if (type && type.length > 0) filters.type = type;
		if (category && category.length > 0) filters.category = category;
		if (state && state.length > 0) filters.state = state;
		if (countryCode) filters.country_code = countryCode;
		if (collection) filters.collection = collection;

		const apiParams = toSnakeCaseKeys({
			...restParams,
			pageSize: 48,
			...(sortBy && { sortBy }),
			...(sortDirection && { sortDirection }),
			...(Object.keys(filters).length > 0 && { filters }),
		});

		const res = (await http.get<PaginatedData<AdminRewardAPIData>>(adminRewardsPath(), { headers, params: apiParams })).data;

		return [res.records, new Metadata(res.metadata)] as const;
	};

export const loadAdminRewards = async (params: AdminRewardsAPIParams = { pageNumber: 1 }) => {
	const queryClient = await getQueryClient();
	return queryClient.fetchQuery({
		queryKey: adminRewardsQueryKey(params),
		queryFn: createAdminRewardFetcher(params),
	});
};

export const useAdminRewards = (params: AdminRewardsAPIParams = { pageNumber: 1 }) => {
	return useQuery({
		queryKey: adminRewardsQueryKey(params),
		queryFn: createAdminRewardFetcher(params),
	});
};

export const useInvalidateAdminRewards = () => {
	const queryClient = useQueryClient();

	return useCallback(() => {
		queryClient.invalidateQueries(adminRewardsQueryKey());
	}, [queryClient]);
};

type MutateAdminRewardAPIParams = {
	id: string;
	state: 'active' | 'inactive';
};

export const useUpdateAdminRewardState = (options: MutationOptions<MutateAdminRewardAPIParams, AdminRewardAPIData> = {}) => {
	const invalidateAdmin = useInvalidateAdminRewards();
	const invalidateRewards = useInvalidateRewards();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (params: MutateAdminRewardAPIParams) => {
			const body = {
				state: params.state,
			};

			options.onMutate?.(params);

			try {
				const http = await getHttpClient('rcs');
				const res = (await http.patch<AdminRewardAPIData>(adminRewardPath(params.id), body, { headers })).data;

				queryClient.setQueriesData<[AdminRewardAPIData[], Metadata]>(adminRewardsQueryKey(), (data) => {
					if (!data) {
						return;
					}

					const updatedItems = data[0].map((item) => {
						if (item.id === params.id) {
							return { ...item, state: params.state };
						}

						return item;
					});

					return [updatedItems, data[1]];
				});

				options.onSuccess?.(res);

				invalidateAdmin();
				invalidateRewards();

				return res;
			} catch (e) {
				options.onError?.(e);
				throw new Error('Failed to update reward state');
			}
		},
	});
};

// Exclusive Rewards

export enum ExclusiveRewardType {
	Swag = 'swag',
	Donation = 'donation',
	Experience = 'experience',
	Other = 'other',
}

export type ExclusiveRewardAudienceRule = {
	type: string;
	values: string | string[];
};

export type ExclusiveRewardVariant = {
	id?: string;
	color?: string;
	size?: string;
	quantity?: number | null;
};

export type ExclusiveRewardAmount = {
	id?: string;
	cents: number;
	quantity?: number | null;
};

export type ExclusiveRewardData = {
	id: string;
	name: string;
	rewardType: ExclusiveRewardType;
	description: string | null;
	website: string | null;
	imageUrl: string | null;
	imageName: string | null;
	imageAltText: string | null;
	collectShippingInfo: boolean;
	fulfillmentPartner: string | null;
	warningMessage: string | null;
	categoryIds: string[];
	audienceRules: ExclusiveRewardAudienceRule[];
	amountCents: number | null;
	variants: ExclusiveRewardVariant[];
	amounts: ExclusiveRewardAmount[];
};

// API params type for create/update mutations (what gets sent to the API)
export type MutateExclusiveRewardAPIData = {
	name: string;
	reward_type: ExclusiveRewardType;
	description?: string | null;
	website?: string | null;
	collect_shipping_info: boolean;
	fulfillment_partner?: string | null;
	warning_message?: string | null;
	category_ids: string[];
	audience_rules: ExclusiveRewardAudienceRule[];
	amount_cents: number | null;
	variants: ExclusiveRewardVariant[];
	amounts: ExclusiveRewardAmount[];
	image?: {
		base64: string;
		content_type: string;
		filename: string;
	};
};

type ExclusiveRewardResponse = {
	data: ExclusiveRewardData;
};

const createExclusiveRewardFetcher = (id: string) => async () => {
	const http = await getHttpClient('rcs');
	const res = await http.get<ExclusiveRewardResponse>(exclusiveRewardPath(id), { headers });
	return res.data.data;
};

export const loadExclusiveReward = async (id: string) => {
	const queryClient = await getQueryClient();
	return queryClient.fetchQuery({
		queryKey: exclusiveRewardQueryKey(id),
		queryFn: createExclusiveRewardFetcher(id),
	});
};

export const useExclusiveReward = (id: string) => {
	const http = useHttpClient('rcs');

	return useQuery({
		queryKey: exclusiveRewardQueryKey(id),
		queryFn: async () => {
			const res = await http.get<ExclusiveRewardResponse>(exclusiveRewardPath(id), { headers });
			return res.data.data;
		},
		enabled: !!id,
	});
};

export const useInvalidateExclusiveRewards = () => {
	const queryClient = useQueryClient();
	const invalidateAdmin = useInvalidateAdminRewards();
	const invalidateMeta = useInvalidateCatalogMetadata();
	const invalidateRewards = useInvalidateRewards();

	return useCallback(() => {
		queryClient.invalidateQueries({ queryKey: exclusiveRewardsQueryKey() });
		// Also invalidate the admin rewards list and catalog metadata since they're affected
		invalidateAdmin();
		invalidateMeta();
		invalidateRewards();
	}, [queryClient, invalidateAdmin, invalidateMeta, invalidateRewards]);
};

export const useInvalidateCatalogMetadata = () => {
	const queryClient = useQueryClient();

	return useCallback(() => {
		queryClient.invalidateQueries({ queryKey: metadataQueryKey() });
	}, [queryClient]);
};

type ExclusiveRewardMutationOptions = {
	onSuccess?: (data: ExclusiveRewardData) => void;
	onError?: (error: unknown) => void;
};

export const useCreateExclusiveReward = (options: ExclusiveRewardMutationOptions = {}) => {
	const http = useHttpClient('rcs');
	const invalidate = useInvalidateExclusiveRewards();

	return useMutation({
		mutationFn: async (data: MutateExclusiveRewardAPIData) => {
			const res = await http.post<ExclusiveRewardResponse>(exclusiveRewardsPath(), data, { headers });
			return res.data.data;
		},
		onSuccess: (data) => {
			invalidate();
			options.onSuccess?.(data);
		},
		onError: options.onError,
	});
};

export const useUpdateExclusiveReward = (id: string, options: ExclusiveRewardMutationOptions = {}) => {
	const http = useHttpClient('rcs');
	const queryClient = useQueryClient();
	const invalidate = useInvalidateExclusiveRewards();

	return useMutation({
		mutationFn: async (data: MutateExclusiveRewardAPIData) => {
			const res = await http.patch<ExclusiveRewardResponse>(exclusiveRewardPath(id), data, { headers });
			return res.data.data;
		},
		onSuccess: (data) => {
			// Update the cache for this specific reward
			queryClient.setQueryData(exclusiveRewardQueryKey(id), data);
			invalidate();
			options.onSuccess?.(data);
		},
		onError: options.onError,
	});
};

export const useDeleteExclusiveReward = (id: string, options: ExclusiveRewardMutationOptions = {}) => {
	const http = useHttpClient('rcs');
	const invalidate = useInvalidateExclusiveRewards();

	return useMutation({
		mutationFn: async () => {
			await http.delete(exclusiveRewardPath(id), { headers });
		},
		onSuccess: () => {
			invalidate();
			options.onSuccess?.({} as ExclusiveRewardData);
		},
		onError: options.onError,
	});
};

// Payment API

/** Mirrors RewardsBillingService::RedemptionRewardParams (POST payment_intents reward_params). Use camelCase; nested values are snake_cased in the request via toSnakeCaseKeys. */
export type PaymentIntentRewardParams = {
	gift: boolean;
	gifteeEmail?: string;
	gifteeFirstName?: string;
	gifteeMessage?: string;
	fulfillmentPartner?: string;
	color?: string;
	size?: string;
	variantData?: {
		type: string;
		pattern?: string;
		color?: string;
		post_wrap_image_id?: string;
	};
	shippingAddressAttributes?: {
		address1?: string;
		address2?: string;
		city?: string;
		state?: string;
		country?: string;
		postalCode?: string;
		phoneNumber?: string;
	};
};

export type CreatePaymentIntentAPIParams = {
	variantId: string;
	rewardParams?: PaymentIntentRewardParams;
};

export type CreatePaymentIntentAPIData = {
	existingPayment: boolean;
	paymentIntentId: string;
	clientSecret: string;
	userRewardPaymentId: string;
	pointsDeducted: number;
	shortage: {
		amountInSubunits: number;
		currency: string;
	};
};

export type PaymentStatusAPIData = {
	status: 'pending' | 'succeeded' | 'failed';
	paymentIntentId: string;
	rewardId: string | null;
	reward: {
		id: string;
		name: string;
		status: string;
	} | null;
	error: string | null;
};

export const useCreatePaymentIntent = () => {
	const http = useHttpClient();

	return useMutation({
		mutationFn: async (data: CreatePaymentIntentAPIParams) => {
			const body: Record<string, unknown> = {
				companyDenominationLegacyId: data.variantId,
			};
			if (data.rewardParams) {
				body.reward_params = toSnakeCaseKeys(data.rewardParams);
			}
			return (await http.post<CreatePaymentIntentAPIData>(createPaymentIntentPath(), body, { headers })).data;
		},
	});
};

export const usePaymentStatus = (paymentIntentId: string | null) => {
	const http = useHttpClient();

	return useQuery({
		queryKey: paymentStatusQueryKey(paymentIntentId),
		queryFn: async () => {
			const res = await http.get<PaymentStatusAPIData>(paymentStatusPath(), {
				params: { payment_intent_id: paymentIntentId },
				headers,
			});
			return res.data;
		},
		enabled: !!paymentIntentId,
		refetchInterval: false,
	});
};
